/**
 * Tutoriel en 4 étapes (mouvement, arme primaire, arme secondaire, collecte), avancé par les actions réelles :
 * une tentative de missile sans lancement ne suffit pas (gamedesign §13).
 */
import type { ShipProfile } from '../data';
import type { SimEvent } from '../sim/events';
import type { PlayerInput } from '../sim/pilots/player';
import type { InputManager } from '../input/input';

export class Tutorial {
  step = 0;
  active: boolean;
  private moved = 0;

  constructor(done: boolean, private readonly profile: ShipProfile, private readonly input: InputManager) {
    this.active = !done;
  }

  skip(): void {
    this.active = false;
  }

  get finished(): boolean { return !this.active || this.step >= 4; }

  observe(input: PlayerInput, events: SimEvent[], dt: number): boolean {
    if (!this.active || this.step >= 4) return false;
    if (this.step === 0) {
      const t = input.thrust;
      if (Math.abs(t.x) + Math.abs(t.y) + Math.abs(t.z) + Math.abs(input.throttleAxis) + Math.abs(input.throttleStep) > 0.2) this.moved += dt;
      if (this.moved > 1.2) this.step = 1;
    }
    for (const e of events) {
      if (this.step === 1) {
        if (this.profile.primary === 'laser' ? e.type === 'laserFired' : e.type === 'missileLaunched' && e.kind === 'missile') this.step = 2;
      } else if (this.step === 2) {
        const wanted = this.profile.secondary === 'swarm' ? 'swarm' : 'missile';
        if (e.type === 'missileLaunched' && e.kind === wanted) this.step = 3;
      } else if (this.step === 3) {
        if (e.type === 'pickupCollected') this.step = 4;
      }
    }
    return this.step >= 4;
  }

  text(gamepad: boolean): string {
    if (!this.active || this.step >= 4) return '';
    const k = (a: Parameters<InputManager['bindingLabel']>[0]) => this.input.bindingLabel(a);
    const primary = this.profile.primary === 'laser' ? 'le laser' : 'un missile (verrou requis)';
    const secondary = this.profile.secondary === 'swarm' ? 'une salve de la nuée (verrou requis)' : 'un missile (verrou requis)';
    const skip = gamepad ? 'Croix gauche : passer' : 'T : passer le tutoriel';
    switch (this.step) {
      case 0:
        return gamepad
          ? `1/4 · Stick gauche : avancer / glisser · Y / X : monter / descendre · Stick droit : orienter\n${skip}`
          : `1/4 · ${k('forward')} / ${k('backward')} ou molette : régler la poussée (${k('throttleZero')} : couper) · ${k('left')} / ${k('right')} : glisser · Souris : piloter\n${skip}`;
      case 1:
        return gamepad ? `2/4 · RT : tirer ${primary}\n${skip}` : `2/4 · Clic gauche : tirer ${primary}\n${skip}`;
      case 2:
        return gamepad
          ? `3/4 · Gardez une cible devant vous jusqu'au verrou, puis LT : ${secondary}\n${skip}`
          : `3/4 · Gardez une cible devant vous jusqu'au verrou, puis clic droit : ${secondary}\n${skip}`;
      default:
        return `4/4 · Récupérez un module orange (ennemi détruit ou Puits Ambre)\n${skip}`;
    }
  }
}
